import dayjs from 'dayjs';
import { OrderRequestResult } from '../../components/Layout/Layout';
import { CallbackTranslation } from '../../locale/localizationContext';
import { WeekDay } from '../../models';

const parseReceptionTime = (receptionTime: string) =>
  dayjs().set('hours', +receptionTime);

export function getTotalHours(
  receptionMorningFromHour: string,
  receptionMorningToHour: string,
  receptionAfternoonFromHour: string,
  receptionAfternoonToHour: string
) {
  const parsedMorningFrom = parseReceptionTime(receptionMorningFromHour);
  const parsedMorningTo = parseReceptionTime(receptionMorningToHour);
  const parsedAfternoonFrom = parseReceptionTime(receptionAfternoonFromHour);
  const parsedAfternoonTo = parseReceptionTime(receptionAfternoonToHour);

  const morningHoursDifference = parsedMorningTo.diff(
    parsedMorningFrom,
    'hours'
  );
  const afternoonHoursDifference = parsedAfternoonTo.diff(
    parsedAfternoonFrom,
    'hours'
  );

  const totalHours = morningHoursDifference + afternoonHoursDifference;
  return totalHours;
}

export type ValidateReceptionHoursParams = {
  receptionMorningFromHour: string;
  receptionMorningToHour: string;
  receptionAfternoonFromHour: string;
  receptionAfternoonToHour: string;
  minimumHours?: number;
  t: CallbackTranslation;
};

export function validateConciergeHours({
  receptionAfternoonFromHour,
  receptionAfternoonToHour,
  receptionMorningFromHour,
  receptionMorningToHour,
  minimumHours,
  t,
}: ValidateReceptionHoursParams) {
  const isMorningFromGreaterThanTo =
    receptionMorningFromHour > receptionMorningToHour;
  const isAfternoonFromGreaterThanTo =
    receptionAfternoonFromHour > receptionAfternoonToHour;

  if (isMorningFromGreaterThanTo || isAfternoonFromGreaterThanTo)
    return t('addressNotesView.fromHourGreaterError');

  const totalHours = getTotalHours(
    receptionMorningFromHour,
    receptionMorningToHour,
    receptionAfternoonFromHour,
    receptionAfternoonToHour
  );

  if (minimumHours && totalHours < minimumHours)
    return t('addressNotesView.receptionHoursError', {
      hours: minimumHours,
    });

  return true;
}

type ExtractedOpeningHours = {
  days: WeekDay[];
  morning?: { from: string; to: string } | null;
  afternoon?: { from: string; to: string } | null;
};

export function extractOpeningHoursFromOrder(orderResult: OrderRequestResult) {
  const isReceptionExist = orderResult.data && orderResult.data.reception;

  if (isReceptionExist) {
    const openingHours = Object.keys(
      orderResult.data!.reception
    ).reduce<ExtractedOpeningHours>(
      (pv, cv) => {
        const hoursSlots = orderResult.data!.reception[cv as WeekDay];

        if (hoursSlots && hoursSlots.length > 0) {
          return {
            morning: hoursSlots[0],
            afternoon: hoursSlots[1],
            days: [...pv.days, cv as WeekDay],
          };
        }

        return pv;
      },
      { days: [] }
    );

    return openingHours.days.length > 0 ? openingHours : null;
  }

  return null;
}
